import { Box, Typography, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import LocaleKeys from '../../../locales/keys';
import { useTranslation } from 'react-i18next';
import { paymentFormName } from '../../../constants/checkout.constant';
import { payloadTypes } from '../../../constants/metaDataPayloadTypes';
import {
  getLeadSourceValue,
  getUserName,
  readSubscriberCountry,
  scrollOptions,
  scrollToTop,
} from '../../../utils/common';
import CONSTANTS from '../../../constants/externalReferences';
import {
  getAdditionalPropertiesList,
  getExistingSubscriptionForUpgrade,
} from '../../../checkout/helpers/paymentOrderHelpers';
import {
  countrySelector,
  getCountryCodeList,
  getSelectedCountry,
  getSelectedTwoLetterCountry,
  getTwoLetterCountryCodeFromThreeLetterCountryCode,
} from '../subscriptionEditCardDetails/country.selectors';
import { stripePaymentTypes } from './paymentTypes.constant';
import {
  AddressElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import React, { useCallback, useEffect, useState } from 'react';
import postMessage from '../../../utils/postMessage';
import StripeCompleteOrderButtonAction from '../stripeCompleteOrderButtonAction';
import StripeChangeCardButtonAction from '../stripeChangeCardButtonAction';
import SubscriptionCheck from '../../../common/SubscriptionCheck';
import ErrorScreen from '../../../common/errorScreen';
import {
  STRIPE_ACCOUNT_COUNTRY_CODE,
  paymentIntentType,
} from '../../../constants/stripePayment.constant';
import {
  getMatchedBillingAddress,
  getDefaultBillingAddressForUpdateSubscription,
  getPayloadWithoutBillingAddress,
  orderPayloadActivity,
  paymentOrderFaultForHandle3DSActivity,
  getStripePaymentType,
  paymentMethodBillingAddress,
  isStripeWallet,
  getPaymentTypesFromStripeList,
} from '../../helpers/paymentMethodStripeHelpers';
import { debounce } from '../../../utils/stripeUtility';
import { setPageLoader } from '../../../app/actions';
import useCreatePaymentInstrument from '../../../common/hooks/useCreatePaymentInstrument';
import { useRef } from 'react';
import useRetrieveAddresses from '../../../common/hooks/useRetrieveAddresses';
import { allowedCardBrandsOrder } from '../../../constants/stripePayment.constant';
import { stripeCardBrandImageUrls } from '../../../utils/common';
import {
  translateCityForSpecificCountries,
  translateStateToCode,
  translateCodeToState,
} from '../../../utils/addressUtils';
import useUpdateMultipleSubscriptions from '../../../common/hooks/useUpdateMultipleSubscriptions';
import requestUpdateMatchingSubscriptions from '../../../utils/requestUpdateMatchingSubscriptions';

const useStyles = makeStyles((theme) => ({
  hidden: {
    display: 'none',
  },
  button: {
    color: '#fff',
    borderRadius: '5px',
    margin: '0',
    fontWeight: 500,
    letterSpacing: 0.5,
    lineHeight: 1,
    padding: '12px 15px',
  },
  buttonContainer: {
    width: '50%',
  },
  saveButton: {
    marginTop: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.custom.darkRed} inset`,
      color: theme.palette.custom.lightBlack,
      background: theme.palette.custom.white,
    },
    '&:disabled': {
      backgroundColor: theme.palette.custom.frame,
    },
  },
  paymentMethodLabelNode: {
    fontFamily: 'Formula1-Regular',
    WebkitFontSmoothing: 'auto',
    fontSize: '25px',
    textTransform: 'uppercase',
    fontWeight: 500,
    padding: '10px 15px',
    [theme.breakpoints.between('xs', 'md')]: {
      fontSize: '19px',
    },
  },
  paymentMethodContainer: {
    flexDirection: 'column',
    marginBottom: 10,
    [theme.breakpoints.between('xs', 'md')]: {
      marginBottom: 0,
    },
  },
  divider: {
    border: `1px  solid ${theme.palette.custom.divider}`,
    marginTop: '20px',
    marginBottom: '20px',
  },
  paymentForm: {
    position: 'relative',
  },
  customBrandedIcons: {
    backgroundColor: theme.palette.custom.white,
    position: 'relative',
    top: '1px',
  },
  cardBrandImage: {
    marginLeft: '2px',
    marginRight: '2px',
    width: '30px',
    height: '20px',
  },
  tabCardLabel: {
    fontFamily: 'titillium-semibold',
    width: '65px',
    backgroundColor: theme.palette.custom.white,
  },
  tabCardLabelFrench: {
    fontFamily: 'titillium-semibold',
    width: '115px',
    backgroundColor: theme.palette.custom.white,
  },
  hidePrivateCardBrandIcons: {
    pointerEvents: 'none',
    backgroundColor: theme.palette.custom.white,
    width: '105px',
    height: '37px',
    zIndex: '1',
    position: 'absolute',
    right: '47px',
    [theme.breakpoints.down('md')]: {
      width: '40px',
    },
  },
  hidePrivateCardBrandIconsFirstPositionSelected: {
    top: '135px',
    transition: 'top 0.45s',
  },
  hidePrivateCardBrandIconsFirstPositionNotSelected: {
    top: '135px',
    transition: 'top 0.45s',
  },
  hidePrivateCardBrandIconsSecondPositionFirstPositionSelected: {
    top: '335px',
    transition: 'top 0.25s ease',
  },
  hidePrivateCardBrandIconsSecondPositionSelected: {
    top: '226px',
    transition: 'bottom 0.10s linear(-0.09 97.06%, 1 100%)',
  },
  hidePrivateCardBrandIconsSecondPositionFirstPositionNotSelected: {
    top: '335px',
    transition: 'top 0.45s',
    [theme.breakpoints.down('md')]: {
      height: '0px',
    },
  },
  hidePrivateCardBrandIconsSecondPositionNotSelected: {
    top: '235px',
    [theme.breakpoints.down('md')]: {
      height: '0px',
    },
  },
  cardTabHeader: {
    position: 'absolute',
    top: '40px',
    left: '33px',
    height: '30px',
    zIndex: '1',
    display: 'flex',
    pointerEvents: 'none',
    [theme.breakpoints.down('md')]: {
      top: '35px',
    },
  },
  cardTabHeaderFirstPosition: {
    top: '40px',
    [theme.breakpoints.down('md')]: {
      top: '35px',
    },
    transition: 'top 0.35s ease',
  },
  cardTabHeaderSecondPositionFirstIsOpen: {
    top: '340px',
    [theme.breakpoints.down('md')]: {
      top: '355px',
    },
    transition: 'top 0.35s ease',
  },
  cardTabHeaderSecondPositionFirstIsClosed: {
    top: '137px',
    [theme.breakpoints.down('md')]: {
      top: '140px',
    },
    transition: 'top 0.35s ease',
  },
  withRadioButton: {
    left: '70px',
  },
  noRadioButton: {
    left: '33px',
  },
}));

const PaymentMethodStripe = (props) => {
  const {
    shoppingCartList,
    staticApiKey,
    staticApiUrl,
    submitOrderUpdateCountryResponse,
    requestSubmitOrderUpdateCountry,
    submitOrderUpdateCountryFault,
    isCheckout,
    handleButtonCancel,
    paymentInstrumentId,
    paymentMethodSaveButtonId,
    handleSaveStripePayment,
    stripePaymentMethodType,
    editSelectedId,
    paymentMethodFormFault,
    handleStripeCheckoutSave,
    isStripePaymentInstrumentEnabled,
    updatePaymentInstrumentFault,
    updatePaymentInstrumentUiModel,
    completeOrderPaymentMethodEditStripeSubmit,
    isCreditCardToStripe,
    subId,
    paymentMethod,
    setShowBackdrop,
    createdPaymentInstrument,
    setCreatedPaymentInstrument,
    redemptionCodesPayload,
    formik,
    showAdditionalSubscriberDetails,
    updateSubscriberResponse,
    setSubmitStripeOrNonStripeCardErrorStatus,
    setIsPaymentReady,
    language,
    externalDataStates,
    updatePaymentInteractionData,
    submitOrderInteractionData,
    currency,
    amount,
    stripePaymentList,
    allSubscriptions,
    upgradePlanState,
  } = props;

  const stripe = useStripe();
  const elements = useElements();
  const subscriberLeadSourceCodes = useSelector(
    (state) =>
      state.ascendon.metadata.code[payloadTypes.subscriberLeadSourceCode],
  );
  const { t } = useTranslation();
  const paymentMethodClasses = useStyles();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isNextActionFailed, setNextActionFailed] = useState(false);
  const [paymentTermsAndConditions, setPaymentTermsAndConditions] = useState({
    paymentTermsAndConditions: '',
  });
  const [stripePayment, setStripePayment] = useState(null);
  const [stripeHandleNextActionResponse, setStripeHandleNextActionResponse] =
    useState(null);
  const [stripePaymentType, setStripePaymentType] = useState(null);
  const stripePaymentTypeRef = useRef(null);
  const [isCompleteOrderButtonDisabled, setIsCompleteOrderButtonDisabled] =
    useState(false);

  const [isPaymentOnReady, setIsPaymentOnReady] = useState(false);
  const [showStripeElement, setShowStripeElement] = useState(false);
  const [subscriptionCheckInProgress, setSubscriptionCheckInProgress] =
    useState(false);
  const [showStripeErrorMessage, setShowStripeErrorMessage] = useState('');
  const [address, setAddress] = useState({});
  const [name, setName] = useState({});
  const [billAddressDefaultValues, setBillAddressDefaultValues] =
    useState(null);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [isAddressElementReady, setIsAddressElementReady] = useState(false);
  const [
    hidePrivateCardBrandIconsDisplayClasses,
    setHidePrivateCardBrandIconsDisplayClasses,
  ] = useState(
    paymentMethodClasses.hidePrivateCardBrandIconsFirstPositionSelected,
  );
  const [cardTabHeaderDisplayClasses, setCardTabHeaderDisplayClasses] =
    useState(paymentMethodClasses.cardTabHeaderFirstPosition);
  const [cardIconLeftOffsetClasses, setCardIconLeftOffsetClasses] = useState(
    paymentMethodClasses.noRadioButton,
  );
  const [paymentMethodsCanMakePayment, setPaymentMethodsCanMakePayment] =
    useState({});

  const doWeHaveAnExistingPaymentInstrument =
    paymentInstrumentId && paymentInstrumentId > 0;

  const {
    countryCodeList,
    selectedCountry,
    selectedTwoLetterCountry,
    sessionCountry,
    metadataCountryCodeList,
  } = useSelector((state) => ({
    countryCodeList: getCountryCodeList(state),
    selectedCountry: getSelectedCountry(state, address?.country),
    selectedTwoLetterCountry: getSelectedTwoLetterCountry(
      state,
      paymentMethod?.BillingAddress?.Country || address?.country,
      paymentMethod?.BillingAddress?.Country,
    ),
    sessionCountry: getTwoLetterCountryCodeFromThreeLetterCountryCode(
      state,
      readSubscriberCountry(),
    ),
    metadataCountryCodeList: countrySelector(state),
  }));

  const stripeAddressElementRef = useRef();
  const paymentElementRef = useRef();

  const [
    createPaymentInstrumentUiModel,
    createPaymentInstrumentFault,
    createPaymentInstrumentLoading,
    requestCreatePaymentInstrument,
    createPaymentInteractionData,
  ] = useCreatePaymentInstrument();

  const [
    retrieveAddressesUiModel,
    retrieveAddressesFault,
    requestRetrieveAddresses,
  ] = useRetrieveAddresses();

  const [
    updateMultipleSubscriptionUiModel,
    updateMultipleSubscriptionFault,
    requestUpdateMultipleSubscriptions,
  ] = useUpdateMultipleSubscriptions();

  const retrieveAddressesUiModelRef = useRef(null);

  const setStripePaymentMethodType = (stripePaymentMethodTypeNode) => {
    const paymentMethodList = Object.values(stripePaymentTypes);
    const getPaymentMethodOrderList = (paymentMethodType, list) => {
      const getWithoutSelectedPaymentType = list.reduce((all, item) => {
        if (paymentMethodType !== item) {
          all.push(item);
        }
        return all;
      }, []);
      if (paymentMethodType) {
        return [paymentMethodType, ...getWithoutSelectedPaymentType];
      } else {
        return list;
      }
    };
    return getPaymentMethodOrderList(
      stripePaymentMethodTypeNode,
      paymentMethodList,
    );
  };

  const stripePaymentMethodValue = setStripePaymentMethodType(
    stripePaymentMethodType,
  );

  useEffect(() => {
    if (!retrieveAddressesUiModelRef.current) {
      requestRetrieveAddresses();
    }
  }, []);

  useEffect(() => {
    const cardElement = elements?.getElement(PaymentElement);
    const addressElement = elements?.getElement(AddressElement);
    if (isPaymentOnReady) {
      if (cardElement) {
        cardElement?.clear();
      }
      if (cardElement && addressElement && !editSelectedId) {
        cardElement?.clear();
        addressElement?.unmount();
      }
    }
  }, [editSelectedId, isPaymentOnReady]);

  useEffect(() => {
    isCompleteOrderButtonDisabledHandler();
  }, [paymentTermsAndConditions]);

  const updateAddress = (event) => {
    setAddress(event.value.address);
    setName(event.value.name);
  };

  const updateCardVarablesOnChange = (type, stripePaymentMethodValue) => {
    updateCardHeaderVariables(type, stripePaymentMethodValue);
  };

  const checkPaymentMethodIsAvailable = (
    paymentMethod,
    paymentMethodsCanMakePayment,
  ) => {
    const translatePaymentMethods = {
      apple_pay: 'applePay',
      google_pay: 'googlePay',
      link: 'link',
    };
    const paymentMethodToCheck = translatePaymentMethods[paymentMethod];

    if (paymentMethodToCheck) {
      return !!paymentMethodsCanMakePayment[paymentMethodToCheck];
    } else {
      return true;
    }
  };

  const updateCardHeaderVariables = (type, stripePaymentMethodValue) => {
    const isDefaultPaymentMethodAvailable = checkPaymentMethodIsAvailable(
      stripePaymentMethodValue[0],
      paymentMethodsCanMakePayment,
    );
    const isCardPaymentFirst =
      stripePaymentMethodValue[0] === stripePaymentTypes.CARD ||
      !isDefaultPaymentMethodAvailable;
    const isFirstPaymentMethodSelected =
      stripePaymentTypeRef.current === stripePaymentMethodValue[0];
    const isCard = type === stripePaymentTypes.CARD;

    if (isCardPaymentFirst) {
      if (isCard) {
        setHidePrivateCardBrandIconsDisplayClasses(
          paymentMethodClasses.hidePrivateCardBrandIconsFirstPositionSelected,
        );
      } else {
        setHidePrivateCardBrandIconsDisplayClasses(
          paymentMethodClasses.hidePrivateCardBrandIconsFirstPositionNotSelected,
        );
      }
      setCardTabHeaderDisplayClasses(
        paymentMethodClasses.cardTabHeaderFirstPosition,
      );
    } else {
      if (isCard) {
        if (isFirstPaymentMethodSelected) {
          setCardTabHeaderDisplayClasses(
            paymentMethodClasses.cardTabHeaderSecondPositionFirstIsOpen,
          );
          setHidePrivateCardBrandIconsDisplayClasses(
            paymentMethodClasses.hidePrivateCardBrandIconsSecondPositionFirstPositionSelected,
          );
        } else {
          setCardTabHeaderDisplayClasses(
            paymentMethodClasses.cardTabHeaderSecondPositionFirstIsClosed,
          );
          setHidePrivateCardBrandIconsDisplayClasses(
            paymentMethodClasses.hidePrivateCardBrandIconsSecondPositionSelected,
          );
        }
      } else {
        if (isFirstPaymentMethodSelected) {
          setHidePrivateCardBrandIconsDisplayClasses(
            paymentMethodClasses.hidePrivateCardBrandIconsSecondPositionFirstPositionNotSelected,
          );
          setCardTabHeaderDisplayClasses(
            paymentMethodClasses.cardTabHeaderSecondPositionFirstIsOpen,
          );
        } else {
          setHidePrivateCardBrandIconsDisplayClasses(
            paymentMethodClasses.hidePrivateCardBrandIconsSecondPositionNotSelected,
          );
          setCardTabHeaderDisplayClasses(
            paymentMethodClasses.cardTabHeaderSecondPositionFirstIsClosed,
          );
        }
      }
    }
  };

  const onPaymentTypeChange = (event) => {
    setStripePaymentType(event.value.type);
    stripePaymentTypeRef.current = event.value.type;

    if (event.value.type === stripePaymentTypes.CARD) {
      const paymentElement = elements.getElement(PaymentElement);
      paymentElement.update({
        fields: {
          billingDetails: {
            address: 'never',
          },
        },
      });
    } else {
      const paymentElement = elements.getElement(PaymentElement);
      paymentElement.update({
        fields: {
          billingDetails: {
            address: 'auto',
          },
        },
      });
    }

    updateCardVarablesOnChange(event.value.type, stripePaymentMethodValue);
  };

  const getAdditionalPropertiesActivity = () => {
    const country = readSubscriberCountry();
    const leadSourceValue = getLeadSourceValue(
      subscriberLeadSourceCodes?.Codes,
    );
    const additionalPropertiesList = [
      { key: CONSTANTS.COUNTRY_CODE, value: country },
      { key: CONSTANTS.LEAD_SOURCE, value: leadSourceValue },
    ];
    const getAdditionalProperties = getAdditionalPropertiesList(
      additionalPropertiesList,
    );
    return { getAdditionalProperties };
  };

  const getSubmitOrderPayload = () => {
    const {
      paymentMethod: { card, id, billing_details },
    } = stripePayment;
    const getType = getStripePaymentType(stripePayment?.paymentMethod);
    const stripeResponse = {
      id,
      type: getType,
      last4: card?.last4,
      exp_month: card?.exp_month,
      exp_year: card?.exp_year,
      brand: card?.brand,
      name: billing_details?.name,
    };
    const { getAdditionalProperties } = getAdditionalPropertiesActivity();
    const submitOrderPayload = orderPayloadActivity(
      {
        shoppingCartList,
        redemptionCodesPayload,
        stripeResponse,
        getAdditionalProperties,
      },
      isCheckout,
      isStripePaymentInstrumentEnabled,
    );

    const isWallet = isStripeWallet(stripePaymentTypeRef.current);
    const { address: walletAddress } = billing_details;
    if (stripePaymentTypeRef.current === stripePaymentTypes.CARD || isWallet) {
      const getStripeBillingAddress = (isWalletPay) =>
        isWalletPay ? walletAddress : address;
      const stripeBillingAddress = getStripeBillingAddress(isWallet);
      const country = selectedCountry?.Value || stripeBillingAddress?.country;

      const translatedState = translateStateToCode({
        country,
        externalDataStates,
        state: stripeBillingAddress?.state,
      });

      const translatedCity = translateCityForSpecificCountries({
        country,
        state: stripeBillingAddress?.state,
        city: stripeBillingAddress?.city,
      });

      const { BillingAddress } = paymentMethodBillingAddress(
        isWallet,
        stripeBillingAddress,
        stripeResponse?.name,
        metadataCountryCodeList,
        country,
        getUserName(),
        { translatedState, translatedCity },
      );

      const matchedBillingAddress = getMatchedBillingAddress({
        newBillingAddress: BillingAddress,
        addressList: retrieveAddressesUiModelRef.current?.Addresses,
      });
      if (matchedBillingAddress) {
        submitOrderPayload.PaymentInstruments = [
          {
            ...submitOrderPayload.PaymentInstruments[0],
            BillingAddressId: matchedBillingAddress.Id,
          },
        ];
      } else {
        submitOrderPayload.PaymentInstruments = [
          {
            ...submitOrderPayload.PaymentInstruments[0],
            BillingAddress,
          },
        ];
      }
    }
    return submitOrderPayload;
  };

  const setBillingAddressDefaultValuesActivity = () => {
    const getDefaultBillingAddress =
      getDefaultBillingAddressForUpdateSubscription(
        paymentMethod,
        selectedTwoLetterCountry || sessionCountry,
      );
    const translatedState = translateCodeToState({
      country: readSubscriberCountry(),
      externalDataStates,
      code: getDefaultBillingAddress?.address?.state,
    });
    if (translatedState) {
      const translatedAddress = {
        name: getDefaultBillingAddress.name,
        address: {
          ...getDefaultBillingAddress.address,
          state: translatedState,
        },
      };
      setBillAddressDefaultValues(translatedAddress);
    } else {
      setBillAddressDefaultValues(getDefaultBillingAddress);
    }
  };

  const mountAddressElementWithDefaultValues = (element, id) => {
    const addressElement = elements?.getElement(element);
    const getAddressElementMountContainer = document.getElementById(
      `address-element-${id}-${subId}`,
    );
    if (getAddressElementMountContainer) {
      if (stripeAddressElementRef.current) {
        addressElement?.unmount();
      }
      setTimeout(() => {
        addressElement?.mount(`#address-element-${id}-${subId}`);
      }, 500);
    }
  };

  useEffect(() => {
    if (paymentMethod) {
      if (editSelectedId === paymentMethod.PaymentInstrumentId) {
        setBillingAddressDefaultValuesActivity();
        mountAddressElementWithDefaultValues(AddressElement, editSelectedId);
      } else {
        if (isCheckout && !isStripePaymentInstrumentEnabled) {
          setBillingAddressDefaultValuesActivity();
        }
      }
    }
  }, [paymentMethod, selectedTwoLetterCountry, editSelectedId, isCheckout]);

  useEffect(() => {
    if (stripeAddressElementRef.current) {
      if (isCheckout && !isStripePaymentInstrumentEnabled) {
        mountAddressElementWithDefaultValues(
          AddressElement,
          paymentInstrumentId,
        );
      }
    }
  }, [stripeAddressElementRef.current]);

  const updateUxBasedOnPaymentCheckResult = (
    result,
    getPaymentMethodFromStripeList,
  ) => {
    const { paypal } = getPaymentMethodFromStripeList;
    const { applePay, googlePay } = result;
    const showRadioButtons = [applePay, googlePay, paypal].some((item) => item);
    setCardIconLeftOffsetClasses(
      showRadioButtons
        ? paymentMethodClasses.withRadioButton
        : paymentMethodClasses.noRadioButton,
    );

    setPaymentMethodsCanMakePayment(result);
  };

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }
    const getPaymentTypes =
      stripePaymentList?.StripePayment?.PaymentTypes || [];

    const getPaymentMethodFromStripeList =
      getPaymentTypesFromStripeList(getPaymentTypes);
    const isListShown = Object.values(getPaymentMethodFromStripeList).some(
      (item) => item,
    );
    if (!isListShown) {
      return;
    }

    try {
      const paymentRequestAmount = amount || 1000;
      const pr = stripe.paymentRequest({
        country: STRIPE_ACCOUNT_COUNTRY_CODE,
        currency,
        total: {
          label: 'F1 Payment',
          amount: paymentRequestAmount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          updateUxBasedOnPaymentCheckResult(
            result,
            getPaymentMethodFromStripeList,
          );
        }
      });
    } catch (e) {
      console.warn(
        `Unsupported Country for Check ${STRIPE_ACCOUNT_COUNTRY_CODE}`,
      );
      updateUxBasedOnPaymentCheckResult(
        {
          applePay: true,
          googlePay: true,
        },
        getPaymentMethodFromStripeList,
      );
    }
  }, [stripe, elements]);

  useEffect(() => {
    if (paymentMethodsCanMakePayment) {
      updateCardVarablesOnChange(
        stripePaymentMethodValue[0],
        stripePaymentMethodValue,
      );
    }
  }, [paymentMethodsCanMakePayment]);

  const submitStripePaymentActivity = (submitOrderPayload) => {
    const haveWeAlreadyCreatedANewPaymentInstrument =
      !!createdPaymentInstrument;
    if (isCheckout && !isStripePaymentInstrumentEnabled) {
      // New stripe PI - first submitOrder call OR
      // New Stripe PI - second submitOrder call
      setIsCompleteOrderButtonDisabled(true);
      requestSubmitOrderUpdateCountry({
        submitOrderUpdateCountryPayload: { ...submitOrderPayload },
        staticApiKey,
        staticApiUrl,
        isUpgrade: upgradePlanState?.isUpgradePlan,
        ...getExistingSubscriptionForUpgrade(upgradePlanState),
      });
    } else if (
      isCreditCardToStripe &&
      !haveWeAlreadyCreatedANewPaymentInstrument
    ) {
      // Creating new stripe PI - existing cybersource to stripe on my subscription
      const [paymentInstrumentPayload] = submitOrderPayload?.PaymentInstruments;
      const { ThreeDSDetail } = submitOrderPayload;
      requestCreatePaymentInstrument({
        PaymentInstrument: { ...paymentInstrumentPayload },
        ThreeDSDetail,
      });
    } else {
      // Updating existing stripe PI - checkout / my subscription
      const [paymentInstrumentPayload] = submitOrderPayload?.PaymentInstruments;
      const { ThreeDSDetail } = submitOrderPayload;
      const updatePaymentInstrumentPayload = {
        PaymentInstrument: {
          ...paymentInstrumentPayload,
          Id: paymentInstrumentId,
        },
        ThreeDSDetail,
      };
      if (isStripePaymentInstrumentEnabled) {
        handleStripeCheckoutSave({
          updatePaymentInstrumentPayload,
        });
      } else {
        handleSaveStripePayment({
          updatePaymentInstrumentPayload,
        });
      }
    }
  };

  useEffect(() => {
    if (stripePayment) {
      const submitOrderPayload = getSubmitOrderPayload();
      setNextActionFailed(false);
      submitStripePaymentActivity(submitOrderPayload);
    }
  }, [stripePayment, isCheckout]);

  useEffect(() => {
    if (submitOrderUpdateCountryResponse) {
      setSubscriptionCheckInProgress(true);
      setStripeHandleNextActionResponse(null);
    }
  }, [submitOrderUpdateCountryResponse]);

  const subscriptionFound = () => {
    if (upgradePlanState?.isUpgradePlan) {
      postMessage(
        'module.orderUpgradeComplete',
        submitOrderUpdateCountryResponse,
      );
    } else {
      postMessage('order.complete', submitOrderUpdateCountryResponse);
    }

    setSubscriptionCheckInProgress(false);
  };

  const subscriptionCheckFailure = () => {
    postMessage('module.error');
    setSubscriptionCheckInProgress(false);
  };

  useEffect(() => {
    if (retrieveAddressesUiModel) {
      retrieveAddressesUiModelRef.current = retrieveAddressesUiModel;
    }
    return () => {
      retrieveAddressesUiModelRef.current = null;
    };
  }, [retrieveAddressesUiModel]);

  useEffect(() => {
    if (retrieveAddressesFault) {
      retrieveAddressesUiModelRef.current = null;
    }
  }, [retrieveAddressesFault]);

  useEffect(() => {
    if (createPaymentInteractionData) {
      setStripeHandleNextActionResponse(null);
    }
  }, [createPaymentInteractionData]);

  useEffect(() => {
    if (updatePaymentInteractionData) {
      setStripeHandleNextActionResponse(null);
    }
  }, [updatePaymentInteractionData]);

  useEffect(() => {
    if (submitOrderInteractionData) {
      setStripeHandleNextActionResponse(null);
    }
  }, [submitOrderInteractionData]);

  useEffect(() => {
    if (submitOrderUpdateCountryFault) {
      if (submitOrderUpdateCountryFault?.Fault?.Code === 635) {
        requestRetrieveAddresses();
        const { stripeClientSecret, stripePaymentId } =
          paymentOrderFaultForHandle3DSActivity(submitOrderUpdateCountryFault);

        setIsCompleteOrderButtonDisabled(false);
        handlePaymentIntentCardAction(stripeClientSecret, stripePaymentId);
      } else {
        if (typeof submitOrderUpdateCountryFault === 'string') {
          setStripePaymentError(submitOrderUpdateCountryFault);
        }
      }
    } else {
      setStripePaymentError('');
    }
  }, [submitOrderUpdateCountryFault]);

  useEffect(() => {
    if (createPaymentInstrumentFault) {
      if (createPaymentInstrumentFault?.Fault?.Code === 635) {
        requestRetrieveAddresses();
        const { stripeClientSecret, stripePaymentId } =
          paymentOrderFaultForHandle3DSActivity(createPaymentInstrumentFault);

        setIsCompleteOrderButtonDisabled(false);
        handlePaymentIntentCardAction(
          stripeClientSecret,
          stripePaymentId,
          true,
        );
      } else {
        if (typeof createPaymentInstrumentFault === 'string') {
          setStripePaymentError(createPaymentInstrumentFault);
        }
      }
    } else {
      setStripePaymentError('');
    }
  }, [createPaymentInstrumentFault]);

  useEffect(() => {
    if (updatePaymentInstrumentFault) {
      if (updatePaymentInstrumentFault?.Fault?.Code === 635) {
        requestRetrieveAddresses();
        const { stripeClientSecret, stripePaymentId } =
          paymentOrderFaultForHandle3DSActivity(updatePaymentInstrumentFault);

        handlePaymentIntentCardAction(
          stripeClientSecret,
          stripePaymentId,
          true,
        );
      } else {
        if (typeof updatePaymentInstrumentFault === 'string') {
          setStripePaymentError(updatePaymentInstrumentFault);
        }
      }
    } else {
      setStripePaymentError('');
    }
  }, [updatePaymentInstrumentFault]);

  useEffect(() => {
    if (createPaymentInstrumentUiModel) {
      const { Id: PaymentInstrumentId } =
        createPaymentInstrumentUiModel?.PaymentInstrument;
      if (setCreatedPaymentInstrument) {
        setCreatedPaymentInstrument(createPaymentInstrumentUiModel);
      }

      if (isStripePaymentInstrumentEnabled) {
        handleStripeCheckoutSave(createPaymentInstrumentUiModel, true);
      } else {
        requestUpdateMatchingSubscriptions({
          requestUpdateMultipleSubscriptions,
          allSubscriptions,
          currentSubscriptionId: subId,
          newPaymentInstrumentId: PaymentInstrumentId,
        });
      }
      setStripeHandleNextActionResponse(null);
    }
  }, [createPaymentInstrumentUiModel]);

  useEffect(() => {
    if (updateMultipleSubscriptionUiModel) {
      handleSaveStripePayment({}, true, updateMultipleSubscriptionUiModel);
    }
  }, [updateMultipleSubscriptionUiModel]);

  useEffect(() => {
    if (updateMultipleSubscriptionFault) {
      setStripePaymentError(updateMultipleSubscriptionFault);
    } else {
      setStripePaymentError('');
    }
  }, [updateMultipleSubscriptionFault]);

  useEffect(() => {
    if (createPaymentInstrumentLoading) {
      postMessage('module.showLoader', {});
    } else {
      postMessage('module.hideLoader', {});
    }
  }, [createPaymentInstrumentLoading]);

  useEffect(() => {
    if (paymentMethodFormFault) {
      setStripePaymentError(paymentMethodFormFault);
    } else {
      setStripePaymentError('');
    }
  }, [paymentMethodFormFault]);

  useEffect(() => {
    if (updatePaymentInstrumentUiModel) {
      const cardElement = elements?.getElement(PaymentElement);
      const addressElement = elements?.getElement(AddressElement);
      cardElement?.clear();
      addressElement?.unmount();
      setStripeHandleNextActionResponse(null);
    }
  }, [updatePaymentInstrumentUiModel]);

  useEffect(() => {
    if (editSelectedId) {
      setStripePaymentError('');
    }
  }, [editSelectedId]);

  const stripeCreatePaymentHandler = async () => {
    const billingAddressDetails = paymentMethod?.BillingAddress;
    const { PostalCode, State, City, LineOne } = billingAddressDetails || {};
    const params = {
      billing_details: {
        name,
        address: {
          country: selectedTwoLetterCountry,
          postal_code: PostalCode || '',
          state: State || '',
          city: City || '',
          line1: LineOne || '',
          line2: '',
        },
      },
    };

    const createPaymentMethodApi = async (selectedPaymentType, params) => {
      if (isStripeWallet(selectedPaymentType)) {
        const walletPaymentMethod = await stripe.createPaymentMethod({
          elements,
        });
        return walletPaymentMethod;
      } else {
        const cardPaymentMethod = await stripe.createPaymentMethod({
          elements,
          params,
        });
        return cardPaymentMethod;
      }
    };

    const createPaymentMethod = createPaymentMethodApi(
      stripePaymentTypeRef.current,
      params,
    );

    createPaymentMethod.then(function (result) {
      console.info('Payment Method: ', result);
      if (result.error) {
        setIsCompleteOrderButtonDisabled(false);
        // Inform the customer that there was an error.
        if (
          result.error.type === 'card_error' ||
          result.error.type === 'validation_error'
        ) {
          console.error(result.error.message);
          setStripePaymentError(result.error.message);
        } else {
          console.error('An unexpected error occurred.');
          setStripePaymentError('An unexpected error occurred.');
        }
        dispatch(setPageLoader(false));
        postMessage('module.hideBackdrop');
        return;
      }

      setStripePayment(result);
    });
    setIsLoading(false);
  };

  useEffect(async () => {
    if (updateSubscriberResponse) {
      stripeCreatePaymentHandler();
    }
  }, [updateSubscriberResponse]);

  const paymentElementOnReady = (e) => {
    setIsPaymentOnReady(true);
    setIsPaymentElementReady(true);
  };

  const addressElementOnReady = (e) => {
    setIsAddressElementReady(true);
  };

  useEffect(() => {
    const onReadyActions = () => {
      setTimeout(() => {
        setShowStripeElement(true);
      }, 0);
      if (setIsPaymentReady) {
        setIsPaymentReady(true);
      }
    };

    if (doWeHaveAnExistingPaymentInstrument && isPaymentElementReady) {
      onReadyActions();
    } else if (isPaymentElementReady && stripeAddressElementRef.current) {
      onReadyActions();
    }
  }, [isPaymentElementReady, isAddressElementReady]);

  const cancelHandler = (e) => {
    setStripePaymentError('');
    const paymentElement = elements.getElement(PaymentElement);
    handleButtonCancel({ paymentElement });
  };
  const isCompleteOrderButtonDisabledHandler = () => {
    const isButtonDisabled =
      !paymentTermsAndConditions.paymentTermsAndConditions;
    setIsCompleteOrderButtonDisabled(isButtonDisabled);
  };

  const handlePaymentTermsAndConditionChecked = (
    paymentTermsAndConditionForm,
  ) => {
    const { formik } = paymentTermsAndConditionForm;
    const { values } = formik;
    setPaymentTermsAndConditions(values);
  };

  const before3DSPopupActions = () => {
    const popupSize = 800;
    postMessage('module.showBackdrop');
    window?.parentIFrame?.autoResize(false);
    window?.parentIFrame?.size(popupSize);
  };

  const after3DSPopupActions = () => {
    postMessage('module.hideBackdrop');
    window?.parentIFrame?.autoResize(true);
  };

  const stripeElementActivity = (stripeElem) => {
    const { paymentIntent, setupIntent, error } = stripeElem;
    try {
      const intent = setupIntent || paymentIntent;
      const errorIntent = error && (error.setup_intent || error.payment_intent);
      if (intent) {
        handleSubmitAfter3DS(intent);
      }
      if (error) {
        setStripePaymentError(error.message);
        setNextActionFailed(true);
        if (errorIntent) {
          handleSubmitAfter3DS(errorIntent);
        }
      }
      if (setShowBackdrop) {
        setShowBackdrop(false);
      }
      after3DSPopupActions();
    } catch (e) {
      console.info(e);
    }
  };

  const resetStripePaymentError = () => {
    setShowStripeErrorMessage(null);
  };

  const setStripePaymentError = (errorMsg) => {
    if (!isNextActionFailed) {
      setShowStripeErrorMessage(errorMsg);
    }
  };

  const stripeClientSecretError = () => {
    console.error('Please specify a client secret!');
    setStripePaymentError('unexpected error occurred');
  };

  const ThreeDSPopupPageScroll = (id) => {
    let pageHeight = document.getElementById(id).offsetHeight;
    scrollToTop(pageHeight / 5, 0, scrollOptions.behavior);
  };

  const handlePaymentIntentCardAction = async (
    stripeClientSecret,
    stripePaymentId,
    isSetupAction,
  ) => {
    try {
      if (stripeClientSecret) {
        if (!isSetupAction) {
          before3DSPopupActions();
        }
        ThreeDSPopupPageScroll(scrollOptions.id);
        const stripeElem = await stripe.handleNextAction({
          clientSecret: stripeClientSecret,
        });
        stripeElementActivity(stripeElem);
        setStripeHandleNextActionResponse(stripeElem);
      } else {
        stripeClientSecretError();
      }
    } catch (e) {
      console.info(e);
    }
  };

  const handleSubmitAfter3DS = (intent) => {
    const threeDSDetail = (intent) => ({
      ThreeDSRedirectUriReference: ASCENDON_ENV_CONFIG.stripe.returnUrl,
      PaymentAuthenticationTransactionId: intent.id,
    });
    if (
      isStripePaymentInstrumentEnabled &&
      intent.object === paymentIntentType.type
    ) {
      // Saved stripe PI - second submitOrder call
      const { getAdditionalProperties } = getAdditionalPropertiesActivity();
      const submitOrderPayload = {
        AdditionalProperties: getAdditionalProperties,
      };
      submitOrderPayload.ThreeDSDetail = threeDSDetail(intent);
      completeOrderPaymentMethodEditStripeSubmit(submitOrderPayload);
    } else {
      // New Stripe PI - second submitOrder call OR
      // Saved stripe PI - second updatePI call
      const submitOrderPayload = getSubmitOrderPayload();
      const findMatchedBillingAddressFromAddressList = getMatchedBillingAddress(
        {
          paymentInstrumentPayload: submitOrderPayload?.PaymentInstruments,
          addressList: retrieveAddressesUiModelRef.current?.Addresses,
        },
      );
      if (findMatchedBillingAddressFromAddressList) {
        const stripePaymentPayloadWithoutBillingAddress =
          getPayloadWithoutBillingAddress(
            submitOrderPayload?.PaymentInstruments?.[0],
          );
        const { payloadWithoutBillingAddress } =
          stripePaymentPayloadWithoutBillingAddress;
        submitOrderPayload.PaymentInstruments = [
          {
            ...payloadWithoutBillingAddress,
            BillingAddressId: findMatchedBillingAddressFromAddressList?.Id,
          },
        ];
      }
      submitOrderPayload.ThreeDSDetail = threeDSDetail(intent);
      submitStripePaymentActivity(submitOrderPayload);
    }
  };

  const isCompleteOrderDisabled = () => {
    setIsCompleteOrderButtonDisabled(
      isCheckout && !isStripePaymentInstrumentEnabled,
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetStripePaymentError();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    isCompleteOrderDisabled();
    dispatch(setPageLoader(true));
    postMessage('module.showLoader');

    // Trigger form validation and wallet collection
    if (stripePaymentTypeRef?.current === stripePaymentTypes.GOOGLE_PAY) {
      postMessage('module.showGooglePayBackdrop');
    }
    const { error: submitError } = await elements.submit();
    if (stripePaymentTypeRef?.current === stripePaymentTypes.GOOGLE_PAY) {
      postMessage('module.hideGooglePayBackdrop');
    }
    if (submitError) {
      console.error(submitError);
      setStripePaymentError(submitError?.message);
      setIsLoading(false);
      setIsCompleteOrderButtonDisabled(false);
      dispatch(setPageLoader(false));
      postMessage('module.hideLoader');
    }

    if (showAdditionalSubscriberDetails) {
      if (submitError) {
        setSubmitStripeOrNonStripeCardErrorStatus(true);
        formik.handleSubmit();
        return;
      }
      setSubmitStripeOrNonStripeCardErrorStatus(false);
      formik.handleSubmit();
    } else {
      await stripeCreatePaymentHandler();
    }
  };

  const debounceFn = useCallback(debounce(handleSubmit, 1000), [
    stripe,
    elements,
    formik,
    showAdditionalSubscriberDetails,
  ]);

  const saveHandler = (e) => {
    if (stripePaymentType === stripePaymentTypes.APPLE_PAY) {
      handleSubmit(e);
    } else {
      debounceFn(e);
    }
  };

  const tabCardLabel =
    language !== 'fr-FR'
      ? paymentMethodClasses.tabCardLabel
      : paymentMethodClasses.tabCardLabelFrench;

  return (
    <>
      <Box>
        {subscriptionCheckInProgress && (
          <SubscriptionCheck
            callback={subscriptionFound}
            faultCallback={subscriptionCheckFailure}
            submitOrderUpdateCountryResponse={submitOrderUpdateCountryResponse}
          ></SubscriptionCheck>
        )}
        {stripe && (
          <Box
            mb={9}
            pt={3}
            className={paymentMethodClasses.paymentMethodContainer}
          >
            {isCheckout && !isStripePaymentInstrumentEnabled && (
              <Box>
                <Typography
                  variant="h2"
                  className={paymentMethodClasses.paymentMethodLabelNode}
                >
                  {t(LocaleKeys.payment.orders.payments)}
                </Typography>
              </Box>
            )}

            <Box
              pt={4}
              className={showStripeElement ? '' : paymentMethodClasses.hidden}
            >
              <form
                id="payment-form"
                className={paymentMethodClasses.paymentForm}
                ref={paymentElementRef}
              >
                <Box
                  className={`${paymentMethodClasses.cardTabHeader} ${cardTabHeaderDisplayClasses} ${cardIconLeftOffsetClasses}`}
                >
                  <Typography variant="h5" className={tabCardLabel}>
                    {t(LocaleKeys.payment.orders.stripePaymentCard)}
                  </Typography>
                  <Box className={paymentMethodClasses.customBrandedIcons}>
                    {allowedCardBrandsOrder.map((item, index) => (
                      <img
                        key={index}
                        src={stripeCardBrandImageUrls(item)}
                        alt={item}
                        className={paymentMethodClasses.cardBrandImage}
                      />
                    ))}
                  </Box>
                </Box>
                <Box
                  className={`${paymentMethodClasses.hidePrivateCardBrandIcons} ${hidePrivateCardBrandIconsDisplayClasses}`}
                ></Box>
                <PaymentElement
                  id="payment-element"
                  options={{
                    layout: {
                      type: 'accordion',
                      radios: true,
                    },
                    fields: {
                      billingDetails: {
                        address: 'never',
                      },
                    },
                    paymentMethodOrder: stripePaymentMethodValue,
                    business: { name: 'F1' },
                  }}
                  onChange={onPaymentTypeChange}
                  onReady={paymentElementOnReady}
                />
                {stripePaymentType === stripePaymentTypes.CARD &&
                  billAddressDefaultValues && (
                    <Box>
                      <Divider className={paymentMethodClasses.divider} />
                      <Typography
                        variant="h2"
                        className={paymentMethodClasses.paymentMethodLabelNode}
                      >
                        {t(
                          LocaleKeys.subscriptionInfo.subscriptions
                            .paymentFormBillingAddress,
                        )}
                      </Typography>
                      <Box
                        mt={5}
                        className="panel-default order-summary-container"
                      >
                        <Box
                          className="panel-body"
                          ref={(element) => {
                            stripeAddressElementRef.current = element;
                          }}
                          id={`address-element-${paymentInstrumentId}-${subId}`}
                        >
                          <AddressElement
                            id={`address-element`}
                            options={{
                              mode: 'billing',
                              allowedCountries: countryCodeList,
                              defaultValues: billAddressDefaultValues,
                            }}
                            onChange={updateAddress}
                            onReady={addressElementOnReady}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                {isCheckout ? (
                  <>
                    {showStripeErrorMessage ? (
                      <Box>
                        <ErrorScreen
                          error={showStripeErrorMessage}
                          mt={5}
                          mb={5}
                          removeStyle={true}
                          variant="h2"
                          hideBackdrop={true}
                        />
                      </Box>
                    ) : null}
                    {isStripePaymentInstrumentEnabled ? (
                      <StripeChangeCardButtonAction
                        cancelHandler={cancelHandler}
                        saveLabel={t(
                          LocaleKeys.subscriptionInfo.subscriptions
                            .paymentFormSave,
                        )}
                        cancelLabel={t(
                          LocaleKeys.subscriptionInfo.subscriptions
                            .paymentFormCancel,
                        )}
                        name={paymentInstrumentId}
                        saveButtonId={paymentMethodSaveButtonId}
                        saveHandler={saveHandler}
                      />
                    ) : (
                      <StripeCompleteOrderButtonAction
                        paymentTermsAndConditions={paymentTermsAndConditions}
                        paymentFormName={paymentFormName}
                        handlePaymentTermsAndConditionChecked={
                          handlePaymentTermsAndConditionChecked
                        }
                        isLoading={isLoading}
                        stripe={stripe}
                        elements={elements}
                        isCompleteOrderButtonDisabled={
                          isCompleteOrderButtonDisabled
                        }
                        handleSubmit={saveHandler}
                        submitOrderUpdateCountryFault={
                          submitOrderUpdateCountryFault
                        }
                        showStripeErrorMessage={showStripeErrorMessage}
                        setPaymentTermsAndConditions={
                          setPaymentTermsAndConditions
                        }
                      />
                    )}
                  </>
                ) : (
                  <>
                    {showStripeErrorMessage ? (
                      <Box>
                        <ErrorScreen
                          error={showStripeErrorMessage}
                          mt={5}
                          mb={5}
                          removeStyle={true}
                          variant="h2"
                          hideBackdrop={true}
                        />
                      </Box>
                    ) : null}
                    <StripeChangeCardButtonAction
                      cancelHandler={cancelHandler}
                      saveLabel={t(
                        LocaleKeys.subscriptionInfo.subscriptions
                          .paymentFormSave,
                      )}
                      cancelLabel={t(
                        LocaleKeys.subscriptionInfo.subscriptions
                          .paymentFormCancel,
                      )}
                      name={paymentInstrumentId}
                      saveButtonId={paymentMethodSaveButtonId}
                      saveHandler={saveHandler}
                    />
                  </>
                )}
              </form>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default PaymentMethodStripe;
